import { fetchWithTimeout } from '../lib/kiosk-stability';
import { useAuthStore } from '../stores/auth';

const API_BASE = '/api/v1';
const DEFAULT_TIMEOUT = 10000; // 10 seconds

interface ApiResponse<T> {
  success: true;
  data: T;
}

interface ApiError {
  success: false;
  error: string | { code: string; message: string };
}

type ApiResult<T> = ApiResponse<T> | ApiError;

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT
): Promise<T> {
  // Only include Content-Type header when there's a body
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetchWithTimeout(
    `${API_BASE}${endpoint}`,
    {
      ...options,
      headers,
      credentials: 'include',
    },
    timeoutMs
  );

  // If the server returns 401, clear auth state so the user is redirected to login
  if (response.status === 401) {
    useAuthStore.getState().setUser(null);
    throw new Error('Unauthorized');
  }

  const data = (await response.json()) as ApiResult<T>;

  if (!data.success) {
    const errorMessage = typeof data.error === 'string'
      ? data.error
      : data.error.message;
    throw new Error(errorMessage);
  }

  return data.data;
}

export const api = {
  get: <T>(endpoint: string) => fetchApi<T>(endpoint),
  post: <T>(endpoint: string, body?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
  patch: <T>(endpoint: string, body: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  put: <T>(endpoint: string, body: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  delete: <T>(endpoint: string, body?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }),
};

// Export apiClient as an alias for use in other modules
export const apiClient = api;

// =============================================================================
// Typed Endpoint Functions
// =============================================================================

import type {
  AuthMeData,
  ConnectedAccountsData,
} from '@my-family-calendar/shared';
import {
  AuthMeResponseSchema,
  ConnectedAccountsResponseSchema,
  validateInDev,
} from '@my-family-calendar/shared';

/**
 * Fetch current authenticated user data
 *
 * GET /api/v1/auth/me
 *
 * Returns the authenticated user's profile with related entities.
 * Validates response shape in development to catch contract violations.
 *
 * CRITICAL: This endpoint was involved in the production bug where
 * App.tsx expected data.data.userId but API returned data.data.id.
 */
export async function fetchAuthMe(): Promise<AuthMeData> {
  const response = await fetchWithTimeout(`${API_BASE}/auth/me`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();

  // Validate response shape in development
  validateInDev(AuthMeResponseSchema, data, '/auth/me');

  if (!data.success) {
    throw new Error(typeof data.error === 'string' ? data.error : data.error.message);
  }

  return data.data as AuthMeData;
}

/**
 * Fetch connected account counts for a family member
 *
 * GET /api/v1/members/:id/connected-accounts
 *
 * Returns the count of connected OAuth accounts (Google/Microsoft) for a member.
 * Used by MemberCalendarActions to show account counts and invitation UI.
 */
export async function fetchConnectedAccounts(memberId: string): Promise<ConnectedAccountsData> {
  const response = await fetchWithTimeout(`${API_BASE}/members/${memberId}/connected-accounts`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();

  // Validate response shape in development
  validateInDev(ConnectedAccountsResponseSchema, data, `/members/${memberId}/connected-accounts`);

  if (!data.success) {
    throw new Error(typeof data.error === 'string' ? data.error : data.error.message);
  }

  return data.data as ConnectedAccountsData;
}
